import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Label } from '@alertwhale/ui'
import { useRegister } from '@/hooks/useAuth'
import { Mail, Lock, User, Building, Loader2 } from 'lucide-react'

export function RegisterPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tenantName, setTenantName] = useState('')
  const register = useRegister()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await register.mutateAsync({ email, password, name, tenantName })
  }

  return (
    <div className="p-8">
      <div className="space-y-2 text-center mb-8">
        <h1 className="text-2xl font-bold text-white">Create an account</h1>
        <p className="text-slate-400">Enter your details to get started</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-slate-300">Full name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500/20"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-300">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500/20"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-slate-300">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500/20"
              required
              minLength={8}
            />
          </div>
          <p className="text-xs text-slate-500">Must be at least 8 characters</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="tenantName" className="text-slate-300">Organization name</Label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="tenantName"
              type="text"
              placeholder="My Company"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500/20"
            />
          </div>
        </div>

        {register.error && (
          <div className="text-sm text-red-400">
            {register.error.message}
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-whale-500 hover:bg-whale-600 text-white font-medium"
          disabled={register.isPending}
        >
          {register.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create account
        </Button>

        <p className="text-center text-sm text-slate-400">
          By signing up, you agree to our{' '}
          <a href="https://alertwhale.com/terms" className="text-whale-400 hover:text-whale-300">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://alertwhale.com/privacy" className="text-whale-400 hover:text-whale-300">
            Privacy Policy
          </a>
        </p>

        <p className="text-center text-sm text-slate-400">
          Already have an account?{' '}
          <Link to="/login" className="text-whale-400 hover:text-whale-300">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  )
}
